import { FC, ReactNode, useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronUp } from '../../assets/icons';
import { classNames } from '../../utils';

interface DropdownItem {
  id: string;
  value: string;
  content: ReactNode;
  text?: string;
}

interface ItemsConfig {
  items?: DropdownItem[];
  displayItems?: DropdownItem[];
  start: number;
  end: number;
}

interface DropdownPropType {
  value?: string;
  placeholder?: string;
  onItemSelect?: (item: DropdownItem) => void;
  items?: DropdownItem[];
  className?: string;
  iconClassName?: string;
  style?: Record<string, unknown>;
  valueText?: string;
  header?: ReactNode;
  footer?: ReactNode;
  itemClassName?: string;
  onOpen?: () => void;
  children?: ReactNode;
  containerClassName?: string;
  itemsContainerClassName?: string;
  itemsWrapperClassName?: string;
  autoClose?: boolean;
  open?: boolean;
  isDisabled?: boolean;
  valueTemplate?: ReactNode;
  itemWrapperStyle?: Record<string, unknown>;
  id: string;
  isActive?: boolean;
  usePortal?: boolean;
}

interface DropdownPosition {
  top: number;
  left: number;
  width: number;
}

// Global state to track which dropdown is open (by id)
let currentOpenDropdownId: string | null = null;
const dropdownCloseCallbacks = new Map<string, () => void>();

const InfiniteScrollDropdown: FC<DropdownPropType> = ({
  id,
  onItemSelect,
  items,
  className,
  value,
  placeholder,
  iconClassName,
  style,
  valueText,
  valueTemplate,
  header,
  footer,
  containerClassName,
  itemClassName,
  itemsContainerClassName,
  itemsWrapperClassName,
  onOpen,
  children,
  isDisabled = false,
  autoClose = true,
  itemWrapperStyle,
  isActive,
  usePortal = false,
}) => {
  const divRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<DropdownPosition>({ top: 0, left: 0, width: 0 });

  const updatePosition = useCallback(() => {
    if (buttonRef.current && usePortal) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [usePortal]);

  // Register close callback for this dropdown
  useEffect(() => {
    const closeDropdown = () => setIsOpen(false);
    dropdownCloseCallbacks.set(id, closeDropdown);
    return () => {
      dropdownCloseCallbacks.delete(id);
    };
  }, [id]);

  // Update global state when this dropdown opens/closes
  useEffect(() => {
    if (isOpen) {
      // Close any other open dropdown
      if (currentOpenDropdownId && currentOpenDropdownId !== id) {
        const closeOther = dropdownCloseCallbacks.get(currentOpenDropdownId);
        closeOther?.();
      }
      currentOpenDropdownId = id;
    } else if (currentOpenDropdownId === id) {
      currentOpenDropdownId = null;
    }
  }, [isOpen, id]);

  useEffect(() => {
    if (isOpen && usePortal) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen, usePortal, updatePosition]);

  const START = 0;
  const END = 20;
  const [itemsConfig, setItemsConfig] = useState<ItemsConfig>({
    items: [],
    displayItems: [],
    start: START,
    end: END,
  });

  const handleScroll = useCallback(() => {
    const div = divRef.current;
    if (div && div.scrollTop + div.clientHeight + 350 >= div.scrollHeight) {
      setItemsConfig((prev) => ({
        ...prev,
        displayItems: [...(prev.items ?? [])].slice(0, prev.end + END),
        end: prev.end + END,
      }));
    }
  }, []);

  useEffect(() => {
    if (items)
      setItemsConfig((prev) => ({
        ...prev,
        items: items,
        displayItems: [...(items ?? [])].slice(0, prev.end),
      }));
  }, [items]);

  // Handle button click to toggle dropdown
  const handleButtonClick = () => {
    if (isDisabled) return;

    updatePosition();
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);

    if (newIsOpen && onOpen && items && items.length > 0) {
      onOpen();
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is inside button
      if (buttonRef.current?.contains(target)) {
        return;
      }

      // Check if click is inside menu (for portal, we need to check the actual menu element)
      if (menuRef.current?.contains(target)) {
        return;
      }

      // Click was outside, close the dropdown
      setIsOpen(false);
    };

    // Use setTimeout to avoid the click that opened the dropdown from closing it
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle scroll for infinite loading
  useEffect(() => {
    const div = divRef.current;
    if (div) {
      div.addEventListener('scroll', handleScroll);
      return () => {
        div.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll, isOpen]);

  const menuContent = (
    <div
      ref={menuRef}
      style={{
        ...(itemWrapperStyle || {}),
        ...(usePortal ? { top: position.top, left: position.left, minWidth: position.width } : {}),
      }}
      className={classNames(
        usePortal ? 'fixed z-[9999]' : 'absolute z-50',
        'mt-2 origin-top-right rounded-xl bg-white dark:bg-slate-800 shadow-xl dark:shadow-slate-950/60 ring-1 ring-gray-200/80 dark:ring-slate-700/80 focus:outline-none backdrop-blur-sm',
        'transition-[opacity,transform] duration-200 ease-out',
        isOpen ? 'opacity-100 visible scale-100' : 'opacity-0 invisible pointer-events-none scale-95',
        itemsWrapperClassName || '',
      )}
    >
      {header && <div className="py-1">{header}</div>}

      <div className={`py-1 ${itemsContainerClassName || ''}`.trim()} ref={divRef}>
        {itemsConfig.displayItems &&
          itemsConfig.displayItems.length > 0 &&
          itemsConfig.displayItems.map((item) => {
            return (
              <div
                key={item.id}
                onClick={() => {
                  if (item.value !== value && onItemSelect) onItemSelect(item);
                  if (autoClose) setIsOpen(false);
                }}
                className={classNames(
                  'cursor-pointer transition-colors duration-150',
                  'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700/50',
                  `block px-4 py-2 text-sm ${
                    value === item.value ? 'bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white' : ''
                  }`.trim(),
                  `${itemClassName || ''}`.trim(),
                )}
              >
                {item.content}
              </div>
            );
          })}
      </div>

      {footer && <div className="py-1">{footer}</div>}
    </div>
  );

  return (
    <div className={`relative text-left ${containerClassName || ''}`.trim()}>
      <button
        ref={buttonRef}
        id={id}
        type="button"
        onClick={handleButtonClick}
        style={style}
        className={classNames(
          `${children ? '' : 'relative '}  ${isDisabled ? `btn-disabled` : ''}`,
          `${className || ''}`,
        )}
      >
        {children || (
          <>
            {isActive && (
              <div className="rounded-full h-[6px] w-[6px] min-h-[6px] min-w-[6px] mr-1 bg-orange dark:bg-teal-400 pointer-events-none" />
            )}

            {valueTemplate || valueText || value || placeholder || ''}

            {!isOpen && items && items.length > 0 && (
              <ChevronDown
                className={`-mr-1 ml-2 pointer-events-none  ${iconClassName || ''}  ${
                  isDisabled ? 'opacity-30' : 'opacity-100'
                }`}
                aria-hidden="true"
              />
            )}
            {!isDisabled && items && items.length > 1 && isOpen && (
              <ChevronUp className={`-mr-1 ml-2 pointer-events-none  ${iconClassName || ''}`} aria-hidden="true" />
            )}
          </>
        )}
      </button>

      {(header !== undefined || footer !== undefined || (items && items.length > 0)) &&
        (usePortal ? createPortal(menuContent, document.body) : menuContent)}
    </div>
  );
};

export default InfiniteScrollDropdown;
